/**
 * Payroll Dashboard
 *
 * Complete dashboard for managing payroll runs with 11-state workflow.
 * FERP Architecture:
 * - Repository pattern (PayrollRepository for all data access)
 * - Backend ID generation (Snowflake IDs only)
 * - Data-driven design (all payroll data from DynamoDB)
 * - Immutable audit trails (SOX/FLSA compliance)
 * - Life-critical safety (payroll affects employee compensation)
 *
 * Issue #354 - Payroll API Implementation
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Badge,
  Tabs,
  Box,
  Alert,
  LoadingOverlay,
  Button,
  Stack,
  Paper,
} from '@mantine/core';
import {
  IconCash,
  IconCalendar,
  IconCheck,
  IconClock,
  IconAlertCircle,
  IconPlus,
  IconRefresh,
  IconFileDownload,
  IconChartBar,
  IconUsers,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import {
  createPayrollRepository,
  PayrollRepository,
  PayrollRun,
  PayrollRunStatus,
} from '../repositories/PayrollRepository';
import { useGenerateId } from '../hooks/useGenerateId';
import { useAuth } from '../security/useAuth';

/**
 * Dashboard props
 * FERP: tenantId required for multi-tenant isolation
 */
export interface PayrollDashboardProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId?: string;
  /** Current user ID for audit trail */
  userId?: string;
  /** Enable automatic polling for real-time updates */
  autoRefresh?: boolean;
  /** Polling interval in milliseconds (default: 5000ms) */
  pollingInterval?: number;
  /** Callback when run is selected */
  onRunSelect?: (runId: string) => void;
  /** Callback when run is created */
  onRunCreate?: (runId: string) => void;
}

/**
 * PayrollDashboard Component
 *
 * Provides comprehensive payroll management:
 * - Payroll run list with status filtering
 * - 11-state workflow management
 * - Time card review
 * - Calculation triggering
 * - Results review
 * - Approval workflow
 * - YTD totals view
 *
 * FERP Compliance:
 * - All data via PayrollRepository (no direct API calls)
 * - Backend Snowflake IDs for all runs
 * - Complete audit trail for all state transitions
 * - Polling-based real-time updates
 * - SOX/FLSA regulatory compliance
 */
export function PayrollDashboard({
  tenantId: propTenantId,
  userId: propUserId,
  autoRefresh = true,
  pollingInterval = 5000,
}: PayrollDashboardProps) {
  // =========================================================================
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)
  // =========================================================================

  // Apollo client for repository
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(
    () => createPayrollRepository(apolloClient),
    [apolloClient]
  );

  // ID generation hook (backend Snowflake IDs)
  const { generateId } = useGenerateId();

  // Get tenant and user from auth context (FERP pattern: no hardcoded values)
  const { tenant, user } = useAuth();

  // Active tab state
  const [activeTab, setActiveTab] = useState<string | null>('active-runs');

  // Payroll runs state
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<PayrollRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Status filter state
  const [statusFilter, setStatusFilter] = useState<PayrollRunStatus | null>(null);

  // Backend availability detection
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // Resolve tenant and user IDs (props override auth context)
  const tenantId = propTenantId || tenant?.id;
  const userId = propUserId || user?.id;

  /**
   * Load payroll runs from backend
   * FERP: All queries through repository
   */
  const loadRuns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const fetchedRuns = statusFilter
        ? await repository.listPayrollRunsByStatus(tenantId, statusFilter, 50)
        : await repository.listPayrollRuns(tenantId, 50);

      setRuns(fetchedRuns);
      setBackendAvailable(true);
      setRetryCount(0);
    } catch (err: any) {
      console.error('Failed to load payroll runs:', err);
      setError(err.message || 'Failed to load payroll runs');
      setBackendAvailable(false);
      setRetryCount((prev) => prev + 1);

      notifications.show({
        title: 'Error',
        message: 'Failed to load payroll runs. Retrying...',
        color: 'red',
        icon: <IconAlertCircle />,
      });
    } finally {
      setLoading(false);
    }
  }, [repository, tenantId, statusFilter]);

  /**
   * Create new payroll run
   * FERP: Backend generates Snowflake ID
   */
  const handleCreateRun = useCallback(async () => {
    try {
      setLoading(true);

      // Generate backend Snowflake ID
      const runIds = await generateId({ entity_type: 'payroll_run', count: 1 });
      const runId = runIds[0];

      // Calculate pay period dates (biweekly)
      const today = new Date();
      const payPeriodEnd = new Date(today);
      payPeriodEnd.setDate(today.getDate() + 14); // 2 weeks from now
      const payPeriodStart = new Date(payPeriodEnd);
      payPeriodStart.setDate(payPeriodEnd.getDate() - 13); // 14 days before end
      const paymentDate = new Date(payPeriodEnd);
      paymentDate.setDate(payPeriodEnd.getDate() + 3); // 3 days after period end

      const run = await repository.createPayrollRun({
        tenantId,
        runId,
        payPeriodStart: payPeriodStart.toISOString().split('T')[0],
        payPeriodEnd: payPeriodEnd.toISOString().split('T')[0],
        paymentDate: paymentDate.toISOString().split('T')[0],
        createdBy: userId,
      });

      setRuns((prev) => [run, ...prev]);
      setSelectedRun(run);

      notifications.show({
        title: 'Success',
        message: 'Payroll run created successfully',
        color: 'green',
        icon: <IconCheck />,
      });

      onRunCreate?.(runId);
    } catch (err: any) {
      console.error('Failed to create payroll run:', err);
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to create payroll run',
        color: 'red',
        icon: <IconAlertCircle />,
      });
    } finally {
      setLoading(false);
    }
  }, [generateId, repository, tenantId, userId, onRunCreate]);

  /**
   * Start time card review
   * FERP: State transition with audit trail
   */
  const handleStartReview = useCallback(
    async (runId: string) => {
      try {
        await repository.startTimeCardReview({
          tenantId,
          runId,
          updatedBy: userId,
        });

        notifications.show({
          title: 'Success',
          message: 'Time card review started',
          color: 'green',
          icon: <IconCheck />,
        });

        await loadRuns();
      } catch (err: any) {
        notifications.show({
          title: 'Error',
          message: err.message || 'Failed to start review',
          color: 'red',
          icon: <IconAlertCircle />,
        });
      }
    },
    [repository, tenantId, userId, loadRuns]
  );

  /**
   * Trigger payroll calculation
   * FERP: Task primitive execution (TimeTrex)
   */
  const handleTriggerCalculation = useCallback(
    async (runId: string) => {
      try {
        await repository.triggerCalculation({
          tenantId,
          runId,
          updatedBy: userId,
        });

        notifications.show({
          title: 'Success',
          message: 'Payroll calculation triggered',
          color: 'green',
          icon: <IconCheck />,
        });

        await loadRuns();
      } catch (err: any) {
        notifications.show({
          title: 'Error',
          message: err.message || 'Failed to trigger calculation',
          color: 'red',
          icon: <IconAlertCircle />,
        });
      }
    },
    [repository, tenantId, userId, loadRuns]
  );

  /**
   * Import results from TimeTrex
   * FERP: ExecutionResult + Context updates
   */
  const handleImportResults = useCallback(
    async (runId: string) => {
      try {
        await repository.importResults({
          tenantId,
          runId,
          updatedBy: userId,
        });

        notifications.show({
          title: 'Success',
          message: 'Results imported successfully',
          color: 'green',
          icon: <IconCheck />,
        });

        await loadRuns();
      } catch (err: any) {
        notifications.show({
          title: 'Error',
          message: err.message || 'Failed to import results',
          color: 'red',
          icon: <IconAlertCircle />,
        });
      }
    },
    [repository, tenantId, userId, loadRuns]
  );

  /**
   * Submit for approval
   * FERP: Workflow state transition
   */
  const handleSubmitForApproval = useCallback(
    async (runId: string) => {
      try {
        await repository.submitForApproval({
          tenantId,
          runId,
          updatedBy: userId,
        });

        notifications.show({
          title: 'Success',
          message: 'Submitted for approval',
          color: 'green',
          icon: <IconCheck />,
        });

        await loadRuns();
      } catch (err: any) {
        notifications.show({
          title: 'Error',
          message: err.message || 'Failed to submit for approval',
          color: 'red',
          icon: <IconAlertCircle />,
        });
      }
    },
    [repository, tenantId, userId, loadRuns]
  );

  /**
   * Approve payroll run
   * FERP: Approval workflow with comments
   */
  const handleApprove = useCallback(
    async (runId: string, comments?: string) => {
      try {
        await repository.approveRun({
          tenantId,
          runId,
          approvedBy: userId,
          comments,
        });

        notifications.show({
          title: 'Success',
          message: 'Payroll run approved',
          color: 'green',
          icon: <IconCheck />,
        });

        await loadRuns();
      } catch (err: any) {
        notifications.show({
          title: 'Error',
          message: err.message || 'Failed to approve run',
          color: 'red',
          icon: <IconAlertCircle />,
        });
      }
    },
    [repository, tenantId, userId, loadRuns]
  );

  /**
   * Mark as paid
   * FERP: Terminal success state
   */
  const handleMarkAsPaid = useCallback(
    async (runId: string) => {
      try {
        await repository.markAsPaid({
          tenantId,
          runId,
          updatedBy: userId,
        });

        notifications.show({
          title: 'Success',
          message: 'Payroll run marked as paid',
          color: 'green',
          icon: <IconCheck />,
        });

        await loadRuns();
      } catch (err: any) {
        notifications.show({
          title: 'Error',
          message: err.message || 'Failed to mark as paid',
          color: 'red',
          icon: <IconAlertCircle />,
        });
      }
    },
    [repository, tenantId, userId, loadRuns]
  );

  // Load runs on mount and when status filter changes
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Auto-refresh polling
  useEffect(() => {
    if (!autoRefresh || !backendAvailable) return;

    const interval = setInterval(() => {
      loadRuns();
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, backendAvailable, pollingInterval, loadRuns]);

  // =========================================================================
  // CONDITIONAL RETURNS (after all hooks are called)
  // =========================================================================

  // Require tenant context for multi-tenant isolation
  if (!tenantId) {
    return (
      <Container>
        <Alert color="red" icon={<IconAlertCircle />}>
          Tenant context required. Please ensure you are authenticated.
        </Alert>
      </Container>
    );
  }

  /**
   * Get status badge color
   */
  const getStatusColor = (status: PayrollRunStatus): string => {
    switch (status) {
      case 'draft':
        return 'gray';
      case 'reviewing_time_cards':
        return 'blue';
      case 'calculating':
        return 'cyan';
      case 'calculation_failed':
        return 'red';
      case 'reviewing_results':
        return 'yellow';
      case 'pending_approval':
        return 'orange';
      case 'revision_requested':
        return 'pink';
      case 'approved':
        return 'green';
      case 'payment_failed':
        return 'red';
      case 'paid':
        return 'teal';
      case 'rejected':
        return 'red';
      default:
        return 'gray';
    }
  };

  /**
   * Get available actions for status
   */
  const getAvailableActions = (status: PayrollRunStatus) => {
    switch (status) {
      case 'draft':
        return ['start_review'];
      case 'reviewing_time_cards':
        return ['trigger_calculation'];
      case 'calculating':
        return ['import_results'];
      case 'reviewing_results':
        return ['submit_for_approval'];
      case 'pending_approval':
        return ['approve', 'reject', 'request_revisions'];
      case 'approved':
        return ['mark_as_paid'];
      default:
        return [];
    }
  };

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Box>
          <Title order={1}>
            <Group gap="xs">
              <IconCash size={32} />
              Payroll Dashboard
            </Group>
          </Title>
          <Text c="dimmed" size="sm" mt="xs">
            Manage payroll runs with 11-state workflow (SOX/FLSA compliant)
          </Text>
        </Box>

        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={loadRuns}
            variant="light"
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateRun}
            disabled={loading}
          >
            New Payroll Run
          </Button>
        </Group>
      </Group>

      {/* Backend status alert */}
      {!backendAvailable && (
        <Alert
          icon={<IconAlertCircle />}
          title="Backend Unavailable"
          color="red"
          mb="md"
        >
          Cannot connect to payroll API. Retrying... (Attempt {retryCount})
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert icon={<IconAlertCircle />} title="Error" color="red" mb="md">
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="active-runs" leftSection={<IconClock size={16} />}>
            Active Runs
          </Tabs.Tab>
          <Tabs.Tab value="pending-approval" leftSection={<IconAlertCircle size={16} />}>
            Pending Approval
          </Tabs.Tab>
          <Tabs.Tab value="completed" leftSection={<IconCheck size={16} />}>
            Completed
          </Tabs.Tab>
          <Tabs.Tab value="ytd" leftSection={<IconChartBar size={16} />}>
            YTD Totals
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="active-runs" pt="md">
          <Stack gap="md">
            {runs
              .filter((r) =>
                ['draft', 'reviewing_time_cards', 'calculating', 'reviewing_results'].includes(
                  r.status
                )
              )
              .map((run) => (
                <Paper key={run.runId} p="md" withBorder>
                  <Group justify="space-between">
                    <Box>
                      <Group gap="xs">
                        <Text fw={600}>Pay Period: {run.payPeriodStart} → {run.payPeriodEnd}</Text>
                        <Badge color={getStatusColor(run.status)}>
                          {run.status.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed" mt="xs">
                        {run.employeeCount} employees • Payment: {run.paymentDate}
                      </Text>
                    </Box>
                    <Group>
                      {getAvailableActions(run.status).includes('start_review') && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => handleStartReview(run.runId)}
                        >
                          Start Review
                        </Button>
                      )}
                      {getAvailableActions(run.status).includes('trigger_calculation') && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => handleTriggerCalculation(run.runId)}
                        >
                          Calculate
                        </Button>
                      )}
                      {getAvailableActions(run.status).includes('import_results') && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => handleImportResults(run.runId)}
                        >
                          Import Results
                        </Button>
                      )}
                      {getAvailableActions(run.status).includes('submit_for_approval') && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => handleSubmitForApproval(run.runId)}
                        >
                          Submit
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Paper>
              ))}
            {runs.filter((r) =>
              ['draft', 'reviewing_time_cards', 'calculating', 'reviewing_results'].includes(
                r.status
              )
            ).length === 0 && (
              <Text c="dimmed" ta="center" py="xl">
                No active payroll runs
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="pending-approval" pt="md">
          <Stack gap="md">
            {runs
              .filter((r) => r.status === 'pending_approval')
              .map((run) => (
                <Paper key={run.runId} p="md" withBorder>
                  <Group justify="space-between">
                    <Box>
                      <Group gap="xs">
                        <Text fw={600}>Pay Period: {run.payPeriodStart} → {run.payPeriodEnd}</Text>
                        <Badge color="orange">PENDING APPROVAL</Badge>
                      </Group>
                      <Text size="sm" c="dimmed" mt="xs">
                        {run.employeeCount} employees • Gross: ${run.totalGrossPay} • Net: $
                        {run.totalNetPay}
                      </Text>
                    </Box>
                    <Group>
                      <Button
                        size="xs"
                        color="green"
                        onClick={() => handleApprove(run.runId)}
                      >
                        Approve
                      </Button>
                    </Group>
                  </Group>
                </Paper>
              ))}
            {runs.filter((r) => r.status === 'pending_approval').length === 0 && (
              <Text c="dimmed" ta="center" py="xl">
                No runs pending approval
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="completed" pt="md">
          <Stack gap="md">
            {runs
              .filter((r) => ['approved', 'paid'].includes(r.status))
              .map((run) => (
                <Paper key={run.runId} p="md" withBorder>
                  <Group justify="space-between">
                    <Box>
                      <Group gap="xs">
                        <Text fw={600}>Pay Period: {run.payPeriodStart} → {run.payPeriodEnd}</Text>
                        <Badge color={getStatusColor(run.status)}>
                          {run.status.toUpperCase()}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed" mt="xs">
                        {run.employeeCount} employees • Gross: ${run.totalGrossPay} • Net: $
                        {run.totalNetPay}
                      </Text>
                    </Box>
                    <Group>
                      {run.status === 'approved' && (
                        <Button
                          size="xs"
                          color="teal"
                          onClick={() => handleMarkAsPaid(run.runId)}
                        >
                          Mark as Paid
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconFileDownload size={14} />}
                      >
                        Export
                      </Button>
                    </Group>
                  </Group>
                </Paper>
              ))}
            {runs.filter((r) => ['approved', 'paid'].includes(r.status)).length === 0 && (
              <Text c="dimmed" ta="center" py="xl">
                No completed payroll runs
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="ytd" pt="md">
          <Text c="dimmed" ta="center" py="xl">
            YTD totals view (to be implemented in Phase 3.4)
          </Text>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
